/**
 * LanguageSelector - Compact language selection for Symptom Checker
 * 
 * A simple, compact language chip that opens a modal for language selection.
 * Designed to match the gear icon style in the Symptom Checker header.
 * 
 * Unlike ChartMind's LanguageSelector (which has provider/patient languages),
 * this is a single-language selector for the patient's preferred language.
 */

import React, { useState } from 'react';
import {
  Box,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Divider,
} from '@mui/material';
import LanguageIcon from '@mui/icons-material/Language';
import CheckIcon from '@mui/icons-material/Check';
import TranslateIcon from '@mui/icons-material/Translate';

import { SUPPORTED_LANGUAGES, getLanguageByCode } from './constants';

/**
 * Compact Language Chip - Shows current language with flag
 * Clicking opens the selection modal
 */
export const LanguageChip = ({ 
  language, 
  onClick, 
  disabled = false,
  size = 'small',
}) => {
  const langInfo = getLanguageByCode(language);
  
  return (
    <Chip
      icon={<LanguageIcon sx={{ fontSize: 16 }} />}
      label={`${langInfo.flag} ${langInfo.label}`}
      onClick={onClick}
      disabled={disabled}
      size={size}
      variant="outlined"
      sx={{
        cursor: disabled ? 'default' : 'pointer',
        '&:hover': disabled ? {} : {
          bgcolor: 'action.hover',
        },
      }}
    />
  );
};

/**
 * Language Selection Modal
 */
const LanguageSelectionModal = ({
  open,
  onClose,
  currentLanguage,
  onLanguageSelect,
}) => {
  const handleSelect = (langCode) => {
    onLanguageSelect(langCode);
    onClose();
  };
  
  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <TranslateIcon color="primary" />
        <Typography variant="h6">Select Language</Typography>
      </DialogTitle>
      
      <DialogContent dividers sx={{ p: 0 }}>
        <Typography 
          variant="body2" 
          color="text.secondary" 
          sx={{ px: 2, py: 1.5, bgcolor: 'grey.50' }}
        >
          Choose your preferred language for the symptom check conversation.
          The system will speak questions in your language and understand your responses.
        </Typography>
        
        <Divider />
        
        <List sx={{ py: 0 }}>
          {SUPPORTED_LANGUAGES.map((lang) => (
            <ListItem 
              key={lang.code} 
              disablePadding
              secondaryAction={
                currentLanguage === lang.code && (
                  <CheckIcon color="primary" sx={{ mr: 1 }} />
                )
              }
            >
              <ListItemButton 
                onClick={() => handleSelect(lang.code)}
                selected={currentLanguage === lang.code}
              >
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <Typography variant="h6">{lang.flag}</Typography>
                </ListItemIcon>
                <ListItemText 
                  primary={lang.label}
                  secondary={
                    lang.sttProvider === 'google' 
                      ? 'Uses Google Cloud Speech' 
                      : null
                  }
                />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
        
        {currentLanguage !== 'en-US' && (
          <>
            <Divider />
            <Box sx={{ px: 2, py: 1.5, bgcolor: 'info.lighter' }}>
              <Typography variant="caption" color="info.dark">
                Note: Your responses will be automatically translated to English 
                for the symptom analysis. Questions will be spoken in {getLanguageByCode(currentLanguage).label}.
              </Typography>
            </Box>
          </>
        )}
      </DialogContent>
      
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
      </DialogActions>
    </Dialog>
  );
};

/**
 * Main LanguageSelector Component
 * Combines the chip and modal
 * 
 * @param {Object} props
 * @param {string} props.language - Current language code
 * @param {Function} props.onChange - Called when language changes
 * @param {boolean} props.disabled - Disable selection
 * @param {boolean} props.compact - Use compact chip style (default: true)
 */
const LanguageSelector = ({
  language,
  onChange,
  disabled = false,
  compact = true,
}) => {
  const [modalOpen, setModalOpen] = useState(false);
  
  const handleOpenModal = () => {
    if (!disabled) {
      setModalOpen(true);
    }
  };
  
  const handleCloseModal = () => {
    setModalOpen(false);
  };
  
  const handleLanguageSelect = (langCode) => {
    onChange?.(langCode);
  };
  
  if (compact) {
    return (
      <>
        <LanguageChip
          language={language}
          onClick={handleOpenModal}
          disabled={disabled}
        />
        
        <LanguageSelectionModal
          open={modalOpen}
          onClose={handleCloseModal}
          currentLanguage={language}
          onLanguageSelect={handleLanguageSelect}
        />
      </>
    );
  }
  
  // Non-compact mode: Show full dropdown (not implemented yet)
  // For now, just use the chip style
  return (
    <>
      <LanguageChip
        language={language}
        onClick={handleOpenModal}
        disabled={disabled}
        size="medium"
      />
      
      <LanguageSelectionModal
        open={modalOpen}
        onClose={handleCloseModal}
        currentLanguage={language}
        onLanguageSelect={handleLanguageSelect}
      />
    </>
  );
};

export default LanguageSelector;
